/**
 * DD Chat Container Component
 *
 * Main container for the Due Diligence chat interface.
 * Supports both compact (fixed height) and flex layout modes.
 */

import React, { useState, useRef, useCallback, useEffect } from 'react';
import {
  View,
  Text,
  ScrollView,
  Pressable,
  KeyboardAvoidingView,
  Platform,
} from 'react-native';
import Animated, {
  FadeIn,
  SlideInRight,
  useAnimatedStyle,
  withSpring,
} from 'react-native-reanimated';
import {
  X,
  Maximize2,
  Minimize2,
  RotateCcw,
  Sparkles,
  ChevronRight,
} from 'lucide-react-native';
import { getUserMessage } from '@/lib/errors';
import { useDDChat } from '@/lib/dd-chat';
import { useThemeColors } from '@/lib/useThemeColors';
import type { DDChatContainerProps, DDQuickAction, DDChatAttachment } from '@/lib/dd-chat/types';
import { DD_QUICK_ACTIONS } from '@/lib/dd-chat/types';
import { DDChatMessage } from './DDChatMessage';
import { DDChatInput } from './DDChatInput';
import AnimatedPressable from '@/components/AnimatedPressable';
import { useFlag } from '@/lib/feature-flags';

export function DDChatContainer({
  startupContext,
  startupId,
  initialCompact = false,
  flexLayout = false,
  onClose,
  investorPreferences,
  allowAttachment = false,
}: DDChatContainerProps) {
  const colors = useThemeColors();
  // Kill-switch / tester-gate for the DD Agent. Defaults true: visible unless an
  // admin explicitly disables it (or gates it to an allowlist) via feature_flags.
  const ddAgentEnabled = useFlag('dd_agent', { default: true });
  const [inputValue, setInputValue] = useState('');
  const [isExpanded, setIsExpanded] = useState(!initialCompact);
  const [attachment, setAttachment] = useState<DDChatAttachment | null>(null);
  const scrollRef = useRef<ScrollView>(null);

  // Initialize chat hook
  const {
    messages,
    isLoading,
    error,
    sendMessage,
    clearHistory,
    suggestedQuestions,
    sessionTokens,
  } = useDDChat({
    startupContext,
    startupId,
    investorPreferences,
    onMessageSent: () => {
      // Scroll to bottom when new message arrives
      setTimeout(() => {
        scrollRef.current?.scrollToEnd({ animated: true });
      }, 100);
    },
  });

  // Handle send
  const handleSend = useCallback(() => {
    if (inputValue.trim() || attachment) {
      // Build message with attachment context
      let messageToSend = inputValue.trim();

      if (attachment) {
        const attachmentContext = `[Analyzing document: ${attachment.name}]\n\n`;
        messageToSend = attachmentContext + (messageToSend || 'Please analyze this pitch deck and provide key insights.');
      }

      sendMessage(messageToSend);
      setInputValue('');
      setAttachment(null);
    }
  }, [inputValue, attachment, sendMessage]);

  // Handle quick action
  const handleQuickAction = useCallback(
    (action: DDQuickAction) => {
      sendMessage(action.prompt);
    },
    [sendMessage]
  );

  // Handle suggestion tap
  const handleSuggestionTap = useCallback(
    (suggestion: string) => {
      sendMessage(suggestion);
    },
    [sendMessage]
  );

  // Handle attachment
  const handleAttach = useCallback((file: DDChatAttachment) => {
    setAttachment(file);
  }, []);

  const handleRemoveAttachment = useCallback(() => {
    setAttachment(null);
  }, []);

  // Auto-scroll on new messages
  useEffect(() => {
    if (messages.length > 0) {
      setTimeout(() => {
        scrollRef.current?.scrollToEnd({ animated: true });
      }, 200);
    }
  }, [messages.length]);

  // Animated expand style (only used when not in flex layout)
  const containerStyle = useAnimatedStyle(() => {
    if (flexLayout) {
      return {}; // No height animation for flex layout
    }
    return {
      height: withSpring(isExpanded ? 600 : 400, {
        damping: 20,
        stiffness: 150,
      }),
    };
  }, [isExpanded, flexLayout]);

  const containerClassName = flexLayout
    ? 'flex-1 rounded-2xl overflow-hidden border'
    : 'rounded-2xl overflow-hidden border';

  // DD Agent disabled via feature flag → render an unavailable state instead of the chat.
  if (!ddAgentEnabled) {
    return (
      <View
        style={[
          !flexLayout && { height: 400 },
          { backgroundColor: colors.surface.primary, borderColor: colors.border.light },
        ]}
        className={`${containerClassName} items-center justify-center p-8`}
      >
        <Sparkles size={28} color={colors.text.tertiary} />
        <Text
          style={{ color: colors.text.primary }}
          className="text-base font-semibold mt-3 text-center"
        >
          AI Chat is temporarily unavailable
        </Text>
        <Text
          style={{ color: colors.text.tertiary }}
          className="text-sm mt-1 text-center"
          numberOfLines={2}
        >
          The DD Agent is paused right now. Your documents and notes are unaffected — please check back shortly.
        </Text>
        {onClose ? (
          <AnimatedPressable
            onPress={onClose}
            style={{ backgroundColor: colors.surface.secondary }}
            className="mt-5 px-5 py-2.5 rounded-xl"
          >
            <Text style={{ color: colors.text.primary }} className="font-medium">Close</Text>
          </AnimatedPressable>
        ) : null}
      </View>
    );
  }

  return (
    <KeyboardAvoidingView
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      className="flex-1"
    >
      <Animated.View
        entering={SlideInRight.duration(300)}
        style={[
          !flexLayout && containerStyle,
          {
            backgroundColor: colors.surface.primary,
            borderColor: colors.border.light,
          },
        ]}
        className={containerClassName}
      >
        {/* Header */}
        <View
          className="flex-row items-center justify-between px-4 py-3 border-b"
          style={{ backgroundColor: colors.surface.secondary, borderColor: colors.border.light }}
        >
          <View className="flex-row items-center gap-2">
            <View
              className="w-8 h-8 rounded-full items-center justify-center"
              style={{ backgroundColor: `${colors.primary[600]}20` }}
            >
              <Sparkles size={16} color={colors.primary[600]} />
            </View>
            <View>
              <Text style={{ color: colors.text.primary }} className="text-sm font-medium">
                DD Assistant
              </Text>
              <Text style={{ color: colors.text.tertiary }} className="text-xs">
                Analyzing {startupContext.name}
              </Text>
            </View>
          </View>

          <View className="flex-row items-center gap-2">
            {/* Token counter */}
            {sessionTokens > 0 && (
              <View
                className="px-2 py-1 rounded"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <Text style={{ color: colors.text.tertiary }} className="text-[10px]">
                  {(sessionTokens / 1000).toFixed(1)}k tokens
                </Text>
              </View>
            )}

            {/* Clear button */}
            {messages.length > 0 && (
              <AnimatedPressable
                onPress={clearHistory}
                className="w-8 h-8 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <RotateCcw size={14} color={colors.text.tertiary} />
              </AnimatedPressable>
            )}

            {/* Expand/collapse (only in fixed height mode) */}
            {!flexLayout && (
              <AnimatedPressable
                onPress={() => setIsExpanded(!isExpanded)}
                className="w-8 h-8 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                {isExpanded ? (
                  <Minimize2 size={14} color={colors.text.tertiary} />
                ) : (
                  <Maximize2 size={14} color={colors.text.tertiary} />
                )}
              </AnimatedPressable>
            )}

            {/* Close button */}
            {onClose && (
              <AnimatedPressable
                onPress={onClose}
                className="w-8 h-8 rounded-full items-center justify-center"
                style={{ backgroundColor: colors.surface.secondary }}
              >
                <X size={14} color={colors.text.tertiary} />
              </AnimatedPressable>
            )}
          </View>
        </View>

        {/* Messages area */}
        <View className="flex-1">
          {messages.length === 0 ? (
            <WelcomeView
              startupName={startupContext.name}
              onQuickAction={handleQuickAction}
              allowAttachment={allowAttachment}
            />
          ) : (
            <ScrollView
              ref={scrollRef}
              className="flex-1 px-4 py-4"
              showsVerticalScrollIndicator={false}
              keyboardShouldPersistTaps="handled"
            >
              {messages.map((msg, index) => (
                <DDChatMessage
                  key={msg.id}
                  message={msg}
                  isLast={index === messages.length - 1}
                  isStreaming={isLoading && index === messages.length - 1}
                />
              ))}

              {/* Loading indicator */}
              {isLoading && messages[messages.length - 1]?.role === 'user' && (
                <LoadingIndicator />
              )}

              {/* Suggested questions */}
              {!isLoading && suggestedQuestions.length > 0 && (
                <SuggestedQuestions
                  questions={suggestedQuestions}
                  onTap={handleSuggestionTap}
                />
              )}

              {/* Error message */}
              {error && <ErrorMessage error={error} />}

              {/* Spacer for scroll */}
              <View className="h-4" />
            </ScrollView>
          )}
        </View>

        {/* Input */}
        <DDChatInput
          value={inputValue}
          onChangeText={setInputValue}
          onSubmit={handleSend}
          disabled={isLoading}
          placeholder={
            messages.length === 0
              ? 'Ask about this startup...'
              : 'Follow-up question...'
          }
          allowAttachment={allowAttachment}
          attachment={attachment}
          onAttach={handleAttach}
          onRemoveAttachment={handleRemoveAttachment}
        />
      </Animated.View>
    </KeyboardAvoidingView>
  );
}

/** Example question for the welcome view */
interface ExampleQuestion {
  question: string;
  category: 'risk' | 'team' | 'market' | 'traction' | 'deal';
}

const EXAMPLE_QUESTIONS: ExampleQuestion[] = [
  { question: 'What are the biggest red flags I should watch for?', category: 'risk' },
  { question: 'Is the team strong enough to execute on this vision?', category: 'team' },
  { question: 'How defensible is their market position?', category: 'market' },
  { question: 'Are their growth metrics impressive for this stage?', category: 'traction' },
  { question: 'What questions should I ask the founders?', category: 'deal' },
];

const CATEGORY_COLORS: Record<ExampleQuestion['category'], string> = {
  risk: '#ef4444',
  team: '#8b5cf6',
  market: '#3b82f6',
  traction: '#10b981',
  deal: '#f59e0b',
};

/**
 * Welcome view with example questions and quick actions
 */
function WelcomeView({
  startupName,
  onQuickAction,
  allowAttachment,
}: {
  startupName: string;
  onQuickAction: (action: DDQuickAction) => void;
  allowAttachment?: boolean;
}) {
  const colors = useThemeColors();

  // Handle example question tap
  const handleExampleTap = useCallback(
    (question: string) => {
      // Create a synthetic quick action to send the question
      onQuickAction({
        id: 'example',
        label: 'Example',
        icon: 'help-circle',
        prompt: question,
        focus: 'general',
      });
    },
    [onQuickAction]
  );

  return (
    <ScrollView
      className="flex-1 px-4 py-4"
      showsVerticalScrollIndicator={false}
    >
      {/* Welcome header */}
      <Animated.View
        entering={FadeIn.delay(100)}
        className="items-center mb-5"
      >
        <View
          className="w-14 h-14 rounded-full items-center justify-center mb-3"
          style={{ backgroundColor: `${colors.primary[600]}15` }}
        >
          <Sparkles size={28} color={colors.primary[600]} />
        </View>
        <Text style={{ color: colors.text.primary }} className="text-lg font-bold text-center">
          Due Diligence Assistant
        </Text>
        <Text style={{ color: colors.text.secondary }} className="text-sm text-center mt-1">
          Analyzing {startupName}
        </Text>
      </Animated.View>

      {/* Capabilities */}
      <Animated.View
        entering={FadeIn.delay(150)}
        className="rounded-xl p-4 mb-5"
        style={{ backgroundColor: colors.surface.secondary }}
      >
        <Text style={{ color: colors.text.secondary }} className="text-sm leading-relaxed">
          I can help you evaluate this investment opportunity. Ask me about:
        </Text>
        <View className="flex-row flex-wrap gap-2 mt-3">
          {['Risks', 'Team', 'Market', 'Traction', 'Competition', 'Valuation'].map((topic, i) => (
            <View
              key={topic}
              className="px-2.5 py-1 rounded-full"
              style={{ backgroundColor: `${colors.primary[600]}12` }}
            >
              <Text style={{ color: colors.primary[600] }} className="text-xs font-medium">
                {topic}
              </Text>
            </View>
          ))}
        </View>
        {allowAttachment && (
          <Text style={{ color: colors.text.tertiary }} className="text-xs mt-3">
            📎 You can also attach a pitch deck for deeper analysis
          </Text>
        )}
      </Animated.View>

      {/* Example questions */}
      <Animated.View entering={FadeIn.delay(200)}>
        <Text
          style={{ color: colors.text.tertiary }}
          className="text-xs font-medium uppercase mb-3"
        >
          Try asking
        </Text>
        <View className="gap-2">
          {EXAMPLE_QUESTIONS.map((item, index) => (
            <Animated.View
              key={index}
              entering={FadeIn.delay(250 + index * 50)}
            >
              <AnimatedPressable
                onPress={() => handleExampleTap(item.question)}
                className="flex-row items-center p-3 rounded-xl border"
                style={{
                  backgroundColor: colors.surface.primary,
                  borderColor: colors.border.light,
                }}
              >
                <View
                  className="w-1.5 h-1.5 rounded-full mr-3"
                  style={{ backgroundColor: CATEGORY_COLORS[item.category] }}
                />
                <Text style={{ color: colors.text.secondary }} className="text-sm flex-1">
                  "{item.question}"
                </Text>
                <ChevronRight size={14} color={colors.text.tertiary} />
              </AnimatedPressable>
            </Animated.View>
          ))}
        </View>
      </Animated.View>

      {/* Quick action chips */}
      <Animated.View entering={FadeIn.delay(450)} className="mt-5">
        <Text
          style={{ color: colors.text.tertiary }}
          className="text-xs font-medium uppercase mb-3"
        >
          Deep Dives
        </Text>
        <View className="flex-row flex-wrap gap-2">
          {DD_QUICK_ACTIONS.slice(0, 4).map((action, index) => (
            <AnimatedPressable
              key={action.id}
              onPress={() => onQuickAction(action)}
              className="px-3 py-2 rounded-lg border"
              style={{
                backgroundColor: colors.surface.secondary,
                borderColor: colors.border.light,
              }}
            >
              <Text style={{ color: colors.text.secondary }} className="text-sm">
                {action.label}
              </Text>
            </AnimatedPressable>
          ))}
        </View>
      </Animated.View>

      {/* Privacy note */}
      <Animated.View entering={FadeIn.delay(550)} className="mt-6 pb-4">
        <Text style={{ color: colors.text.tertiary }} className="text-xs text-center">
          🔒 Private session • Analysis not stored
        </Text>
      </Animated.View>
    </ScrollView>
  );
}

/**
 * Loading indicator
 */
function LoadingIndicator() {
  const colors = useThemeColors();

  return (
    <Animated.View
      entering={FadeIn}
      className="flex-row items-center gap-3 px-4 py-3 rounded-xl mb-4"
      style={{ backgroundColor: colors.surface.secondary }}
    >
      <View
        className="w-6 h-6 rounded-full items-center justify-center"
        style={{ backgroundColor: `${colors.primary[600]}20` }}
      >
        <Sparkles size={12} color={colors.primary[600]} />
      </View>
      <View className="flex-row gap-1">
        <View
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: colors.primary[600], opacity: 0.6 }}
        />
        <View
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: colors.primary[600], opacity: 0.6 }}
        />
        <View
          className="w-2 h-2 rounded-full"
          style={{ backgroundColor: colors.primary[600], opacity: 0.6 }}
        />
      </View>
      <Text style={{ color: colors.text.tertiary }} className="text-sm">
        Analyzing...
      </Text>
    </Animated.View>
  );
}

/**
 * Suggested questions
 */
function SuggestedQuestions({
  questions,
  onTap,
}: {
  questions: string[];
  onTap: (question: string) => void;
}) {
  const colors = useThemeColors();

  return (
    <Animated.View entering={FadeIn} className="mt-2 mb-2">
      <Text style={{ color: colors.text.tertiary }} className="text-xs mb-2">
        Suggested follow-ups:
      </Text>
      <View className="gap-2">
        {questions.slice(0, 3).map((question, index) => (
          <AnimatedPressable
            key={index}
            onPress={() => onTap(question)}
            className="px-3 py-2 rounded-lg border"
            style={{
              backgroundColor: colors.surface.secondary,
              borderColor: colors.border.light,
            }}
          >
            <Text style={{ color: colors.text.secondary }} className="text-sm">
              {question}
            </Text>
          </AnimatedPressable>
        ))}
      </View>
    </Animated.View>
  );
}

/**
 * Error message
 */
function ErrorMessage({ error }: { error: Error }) {
  const colors = useThemeColors();

  return (
    <Animated.View
      entering={FadeIn}
      className="px-4 py-3 rounded-xl border mb-4"
      style={{
        backgroundColor: `${colors.error}10`,
        borderColor: `${colors.error}30`,
      }}
    >
      <Text style={{ color: colors.error }} className="text-sm">
        {getUserMessage(error)}
      </Text>
    </Animated.View>
  );
}
